import { useQueryClient } from '@tanstack/react-query'
import moment from 'moment'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePSNavigate } from '../../hooks/usePSNavigate'
import { RecordFactory } from '../../models/RecordFactory'
import { useOnboardingContext } from '../../providers/OnboardingProvider'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Heading from '../app/design/Heading'
import Paragraph from '../app/design/Paragraph'
import { AddressPicker } from '../common/AddressPicker'
import { DatePicker } from '../common/fields/DatePicker'
import { TextField } from '../common/fields/TextField'
import { WorkflowPage } from '../workflows/WorkflowPage'

export function SetupDashboardVerifyPersonalPage() {
    const navigate = usePSNavigate()
    const { growYourBusinessDetails, setGrowYourBusinessDetails } = useOnboardingContext()
    const { logAndDisplayError } = useErrorHandler()

    const [isSaving, setIsSaving] = useState(false)
    const [addressSearchValue, setAddressSearchValue] = useState('')

    const [searchParams] = useSearchParams()
    const banner = searchParams.get('banner')
    const queryClient = useQueryClient()

    const saveEnabled = useMemo(() => {
        return (
            !!growYourBusinessDetails?.legalName &&
            growYourBusinessDetails?.ssnLast4?.length === 4 &&
            !!growYourBusinessDetails?.address &&
            !!growYourBusinessDetails?.birthday
        )
    }, [growYourBusinessDetails])

    const onNextHandler = async () => {
        if (
            !growYourBusinessDetails?.legalName ||
            !growYourBusinessDetails?.address ||
            !growYourBusinessDetails?.ssnLast4 ||
            !growYourBusinessDetails?.birthday ||
            isSaving
        ) {
            return
        }

        // only save here if they're coming from the banner
        // otherwise we will save on the next step with the photo verification
        if (banner) {
            setIsSaving(true)
            const result = await RecordFactory.getInstance().submitVerification(
                PSApplication.me(),
                growYourBusinessDetails?.legalName,
                growYourBusinessDetails?.address,
                moment(growYourBusinessDetails?.birthday),
                growYourBusinessDetails?.ssnLast4,
                undefined
            )
            setIsSaving(false)

            if (!result.ok) {
                logAndDisplayError(result.error)
                return
            }

            if (result.value?.verifiedDate) {
                await queryClient.invalidateQueries({ queryKey: ['dashboard'] })

                // do not continue to the next step if they're already verified
                return navigate('/')
            }
        }

        return navigate(
            banner ? `/golive/photo-verification?banner=${banner}` : '/golive/photo-verification'
        )
    }

    return (
        <WorkflowPage
            onClose={() => navigate('/')}
            onBack={() => navigate('/golive/business-info')}
            backButtonTitle="Business Info"
            footerContent={
                <div className="flex justify-end gap-x-4">
                    <Button
                        intent="secondary"
                        type="submit"
                        disabled={isSaving}
                        onClick={async () => {
                            return navigate(
                                banner
                                    ? `/golive/photo-verification?banner=${banner}`
                                    : '/golive/photo-verification'
                            )
                        }}
                    >
                        Skip
                    </Button>
                    <Button
                        intent="primary"
                        type="submit"
                        isLoading={isSaving}
                        disabled={!saveEnabled}
                        onClick={onNextHandler}
                    >
                        Next
                    </Button>
                </div>
            }
        >
            <Heading size="xl" className="mt-[5px]">
                Tell us about yourself
            </Heading>
            <Paragraph size="md" className="mt-4 max-w-[580px] text-center text-gray-600">
                Once verified you’ll be able to cash out payments & see those payments hit your
                account within 15 minutes.
            </Paragraph>
            <WorkflowPage.Card className="mt-12">
                <WorkflowPage.IconSection title="Full name (required)" icon="user.large">
                    <TextField
                        value={growYourBusinessDetails?.legalName}
                        onChange={(e) =>
                            setGrowYourBusinessDetails((s) => ({
                                ...s,
                                legalName: e.target.value,
                            }))
                        }
                        onClear={() =>
                            setGrowYourBusinessDetails((s) => ({
                                ...s,
                                legalName: '',
                            }))
                        }
                        placeholder="Enter full name"
                        size="medium"
                    />
                </WorkflowPage.IconSection>
                <WorkflowPage.IconSection title="What's your address (required)" icon="home.large">
                    <AddressPicker
                        size="medium"
                        defaultAddress={growYourBusinessDetails?.address}
                        onSelect={(address) =>
                            setGrowYourBusinessDetails((s) => ({ ...s, address }))
                        }
                        placeholder="Enter address"
                        searchValue={addressSearchValue}
                        setSearchValue={setAddressSearchValue}
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection
                    title="What's your date of birth (required)"
                    description="We need to verify your identity and ensure secure payout."
                    icon="calendar.large"
                >
                    <DatePicker
                        size="medium"
                        selectedDateString={growYourBusinessDetails?.birthday?.toISOString()}
                        onSelectDate={(date) =>
                            setGrowYourBusinessDetails((s) => ({
                                ...s,
                                birthday: date,
                            }))
                        }
                        placeholder="Select date of birth"
                        maxDateString={moment().subtract(18, 'years').toISOString()}
                        startDateString={moment().subtract(18, 'years').toISOString()}
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection
                    title="What are your last 4 digits of your SSN (required)"
                    icon="employeeCard.large"
                    description="We need to verify your identity and ensure secure payout."
                >
                    <TextField
                        value={growYourBusinessDetails?.ssnLast4}
                        onChange={(e) =>
                            setGrowYourBusinessDetails((s) => ({
                                ...s,
                                ssnLast4: e.target.value,
                            }))
                        }
                        onClear={() =>
                            setGrowYourBusinessDetails((s) => ({
                                ...s,
                                ssnLast4: '',
                            }))
                        }
                        placeholder="Enter last 4 digits of SSN"
                        size="medium"
                        maxLength={4}
                        mask={'0000'}
                    />
                </WorkflowPage.IconSection>
            </WorkflowPage.Card>
        </WorkflowPage>
    )
}
